import type { ReactNode } from 'react';
import {
    Box,
    Chip,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { formatRange } from '../../components/charts/chartFormat';
import { RESOLUTION_OPTIONS } from '../../components/charts/convergence/constants';
import { COARSE, MIN_TARGET_PX } from '../../../../root/touchTheme';
import { QUICK_RANGES } from '../lifetime';
import { CompactChartToolbar } from './CompactChartToolbar';
import type { ProbabilityMode } from './series/probability';
import { useCompactChart } from './useCompactChart';

const AUTO = 'auto';

export interface QuickRangeButton {
    id: string;
    label: string;
    disabled: boolean;
    active: boolean;
}

interface ChartToolbarProps {
    displayRange: [Date, Date];
    zoomed: boolean;
    quickRanges: readonly QuickRangeButton[];
    onQuickRange: (id: string) => void;
    mode: ProbabilityMode;
    candlesAllowed: boolean;
    stackedAllowed: boolean;
    onModeChange: (mode: ProbabilityMode) => void;
    resolutionChoice: number | null;
    resolutionLabel: string;
    onResolutionChange: (choice: number | null) => void;
    showVolume: boolean;
    onToggleVolume: () => void;
    exportSlot?: ReactNode;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    children?: ReactNode;
}

const toggleGroupSx = {
    height: 26,
    '& .MuiToggleButton-root': {
        py: '2px',
        px: 1,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'none' as const,
        borderColor: 'divider',
    },
};

/** Why a view is greyed out; the desktop tooltip and the phone sheet say the same. */
export function viewHint(
    candlesAllowed: boolean,
    stackedAllowed: boolean
): string {
    return [
        candlesAllowed
            ? ''
            : 'Candles are available when one outcome is selected.',
        stackedAllowed
            ? ''
            : 'Stacked is not available here: the outcomes of this event do not add up to 100%. Select one outcome to use it.',
    ]
        .filter(Boolean)
        .join(' ');
}

function DesktopChartToolbar(props: ChartToolbarProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 0.5,
            }}
        >
            {props.children}
            <Box sx={{ flex: 1 }} />
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    color: props.zoomed ? 'text.primary' : 'text.secondary',
                    fontWeight: props.zoomed ? 600 : 400,
                }}
            >
                {formatRange(props.displayRange)}
            </Typography>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={props.quickRanges.find(q => q.active)?.id ?? null}
                onChange={(_, value: string | null) => {
                    if (value) props.onQuickRange(value);
                }}
                sx={toggleGroupSx}
            >
                {props.quickRanges.map(q => (
                    <ToggleButton
                        key={q.id}
                        value={q.id}
                        disabled={q.disabled}
                        aria-label={`Time range: ${q.label}`}
                    >
                        {q.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Chip
                label="Volume"
                size="small"
                onClick={props.onToggleVolume}
                color={props.showVolume ? 'primary' : 'default'}
                variant={props.showVolume ? 'filled' : 'outlined'}
                sx={{ height: 22, fontSize: '10px', fontWeight: 600 }}
            />
            <Tooltip
                title={viewHint(props.candlesAllowed, props.stackedAllowed)}
            >
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={props.mode}
                    onChange={(_, value: ProbabilityMode | null) => {
                        if (value) props.onModeChange(value);
                    }}
                    sx={toggleGroupSx}
                >
                    <ToggleButton
                        value="stacked"
                        disabled={!props.stackedAllowed}
                    >
                        Stacked
                    </ToggleButton>
                    <ToggleButton value="line">Line</ToggleButton>
                    <ToggleButton
                        value="candles"
                        disabled={!props.candlesAllowed}
                    >
                        Candles
                    </ToggleButton>
                </ToggleButtonGroup>
            </Tooltip>
            <Select
                value={props.resolutionChoice ?? AUTO}
                onChange={event => {
                    const value = event.target.value;
                    props.onResolutionChange(
                        value === AUTO ? null : Number(value)
                    );
                }}
                size="small"
                variant="outlined"
                renderValue={value =>
                    value === AUTO
                        ? `Auto (${props.resolutionLabel})`
                        : props.resolutionLabel
                }
                sx={{
                    height: 26,
                    // in a row of 44 px buttons on a touch screen
                    [COARSE]: { height: MIN_TARGET_PX },
                    fontSize: '11px',
                    fontWeight: 600,
                    '& .MuiSelect-select': { py: '2px', px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                    },
                }}
            >
                <MenuItem value={AUTO} sx={{ fontSize: '12px' }}>
                    Auto
                </MenuItem>
                {RESOLUTION_OPTIONS.map(option => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: '12px' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            {props.exportSlot}
            <Tooltip
                title={props.fullscreen ? 'Exit full screen' : 'Full screen'}
            >
                <IconButton
                    size="small"
                    onClick={props.onToggleFullscreen}
                    sx={{ p: 0.25 }}
                >
                    {props.fullscreen ? (
                        <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    ) : (
                        <FullscreenIcon sx={{ fontSize: 18 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

export function ChartToolbar(props: ChartToolbarProps) {
    const compact = useCompactChart();
    if (!compact) return <DesktopChartToolbar {...props} />;
    return (
        <CompactChartToolbar
            ranges={props.quickRanges}
            activeRange={props.quickRanges.find(q => q.active)?.id ?? null}
            onRange={props.onQuickRange}
            options={{
                view: {
                    value: props.mode,
                    options: [
                        {
                            value: 'stacked',
                            label: 'Stacked',
                            disabled: !props.stackedAllowed,
                        },
                        { value: 'line', label: 'Line' },
                        {
                            value: 'candles',
                            label: 'Candles',
                            disabled: !props.candlesAllowed,
                        },
                    ],
                    onChange: props.onModeChange,
                    hint:
                        viewHint(props.candlesAllowed, props.stackedAllowed) ||
                        undefined,
                },
                resolution: {
                    choice: props.resolutionChoice,
                    label: props.resolutionLabel,
                    onChange: props.onResolutionChange,
                },
                volume: {
                    shown: props.showVolume,
                    onToggle: props.onToggleVolume,
                },
                download: props.exportSlot,
            }}
            fullscreen={props.fullscreen}
            onToggleFullscreen={props.onToggleFullscreen}
            extra={props.children}
        />
    );
}

function noop() {}

const PLACEHOLDER_RANGES: readonly QuickRangeButton[] = [
    ...QUICK_RANGES.map(q => ({
        id: q.id,
        label: q.label,
        disabled: true,
        active: false,
    })),
    { id: 'all', label: 'All', disabled: true, active: true },
];
const PLACEHOLDER_RANGE: [Date, Date] = [new Date(0), new Date(0)];

/**
 * The toolbar's place while the chart loads: an invisible toolbar holds the
 * height (desktop or touch; the desktop one wraps on a narrow card, so no
 * fixed height would do) and a placeholder covers it. Its buttons and test ids
 * are in the page, hidden: a test that reads the toolbar waits for the chart.
 */
export function ChartToolbarPlaceholder() {
    return (
        <Box
            data-testid="chart-toolbar-placeholder"
            aria-hidden
            sx={{ position: 'relative' }}
        >
            <Box sx={{ visibility: 'hidden' }}>
                <ChartToolbar
                    displayRange={PLACEHOLDER_RANGE}
                    zoomed={false}
                    quickRanges={PLACEHOLDER_RANGES}
                    onQuickRange={noop}
                    mode="line"
                    candlesAllowed={false}
                    stackedAllowed
                    onModeChange={noop}
                    resolutionChoice={null}
                    resolutionLabel="Auto"
                    onResolutionChange={noop}
                    showVolume={false}
                    onToggleVolume={noop}
                    fullscreen={false}
                    onToggleFullscreen={noop}
                />
            </Box>
            <Skeleton
                variant="rounded"
                sx={{ position: 'absolute', inset: 0, height: 'auto' }}
            />
        </Box>
    );
}
